/**
 * The HH Electrical Systems logo.
 *
 * Geometry measured off the client's photograph of the printed logo; the canonical file
 * is brand/logo/hh-mark.svg and the measurements are in brand/LOGO.md. Inlined as a
 * component rather than loaded as an <img> so it inherits `currentColor` — the sidebar
 * shows it on a light surface in one theme and a dark one in the other.
 *
 * `Mark` is the triangle alone. `Logo` is the full printed lockup, wordmark included.
 */

/*
 * Three subpaths: the triangle ring, then one outline per H.
 *
 * The ring is traced as a SINGLE closed outline with the slot already taken out of the
 * left side — not "outer minus inner minus slot". The slot's right edge lies exactly on
 * the inner triangle's left edge, and two coincident edges under evenodd render as a
 * hairline seam, visible as a white diagonal across the second H. Each H is one outline
 * for the same reason.
 *
 * Generated from brand/logo/hh-mark.svg — edit there, not here.
 */
const MARK_PATH = `
  M500 0 L1000 889.8 L0 889.8 L66.36 771.7 L201.83 771.7 L798.17 771.7 L500 241.08
  L265.05 659.2 L129.58 659.2 Z M120.2 684 L136.1 684 L136.1 716.8 L159.6 716.8 L159.6
  684 L175.5 684 L175.5 757.1 L159.6 757.1 L159.6 734.8 L136.1 734.8 L136.1 757.1 L120.2
  757.1 Z M188.9 684 L204.8 684 L204.8 716.8 L228.3 716.8 L228.3 684 L244.2 684 L244.2
  757.1 L228.3 757.1 L228.3 734.8 L204.8 734.8 L204.8 757.1 L188.9 757.1 Z
`;

export function Mark({ size = 28, className = '' }: { size?: number; className?: string }) {
  return (
    <svg
      viewBox="0 0 1000 889.8"
      // 1000 : 889.8 is the measured aspect — taller than equilateral.
      height={size}
      width={size * 1.1239}
      role="img"
      aria-label="HH Electrical Systems"
      className={className}
    >
      <path fill="currentColor" fillRule="evenodd" d={MARK_PATH} />
    </svg>
  );
}

/**
 * The full lockup: mark above, ELECTRICAL SYSTEMS beneath at the width of the triangle's
 * base, exactly as the printed logo sets it.
 *
 * The wordmark is live text rather than outlines — the client has not supplied the
 * original vector file, so the face is matched (bold neo-grotesque) rather than
 * reproduced. `textLength` pins its total width to the triangle's base, so a font
 * fallback shifts the tracking but never the layout. See brand/LOGO.md.
 */
export function Logo({ size = 44, className = '' }: { size?: number; className?: string }) {
  return (
    <svg
      viewBox="0 0 1000 992"
      height={size}
      width={Math.round(size * (1000 / 992) * 100) / 100}
      role="img"
      aria-label="HH Electrical Systems"
      className={className}
    >
      <path fill="currentColor" fillRule="evenodd" d={MARK_PATH} />
      <text
        x="7.38"
        y="992"
        textLength="980.62"
        lengthAdjust="spacing"
        fill="currentColor"
        fontFamily="Arial, Helvetica, 'Helvetica Neue', 'Liberation Sans', sans-serif"
        fontWeight="700"
        fontSize="85.5"
      >
        ELECTRICAL SYSTEMS
      </text>
    </svg>
  );
}
